import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';

import { sortUnitTypes } from '@/features/rapidstor/helpers';
import {
    DescriptorOptions,
    unitOptionsTransformer,
    UpgradeDescriptor,
} from '@/features/rapidstor/UnitOptions';

/**
 * Which kind of upsell the customise-booking screen puts in front of the visitor.
 * The three are ordered by precedence: a descriptor upsell or a unit-type upgrade
 * suppresses the next-size-up card.
 */
export type DisplayedOptionsKind =
    | 'descriptor'
    | 'unit-type'
    | 'next-size-up'
    | 'none';

export interface DisplayedOptions {
    kind: DisplayedOptionsKind;
    /** Descriptor upgrades rendered as options. Empty unless the descriptor upsells. */
    descriptorOptions: UpgradeDescriptor[];
    /** Alternative unit types rendered as options, minus the one already reserved. */
    unitTypeOptions: UnitType[];
    /** The next-size-up descriptor and the unit type that represents it, when it is displayed. */
    nextSizeUp: { descriptor: Descriptor; unitType: UnitType } | null;
    /**
     * The unit type that represents `upSell`, whenever it has one. Present even when the
     * next-size-up card is suppressed, because the "Upsize for an extra ..." copy uses it
     * regardless of which options are on screen.
     */
    nextSizeUpUnitType: UnitType | undefined;
    /** True when at least one rendered option is available. Gates the desktop OptionsCard. */
    hasMultipleOptions: boolean;
}

export interface ResolveDisplayedOptionsArgs {
    descriptor: Descriptor;
    descriptors: Descriptor[];
    location: Location;
    /** `reservationData.unitType` — the selection the options are alternatives to. */
    reservedUnitType: UnitType;
    /** The redux `upSell` descriptor, i.e. the next size up. */
    upSell: Descriptor | null;
}

/**
 * Single source of truth for what the customise-booking screen displays.
 *
 * OptionSelector mounts twice on that screen (mobile and desktop), so tracking cannot
 * live inside it. Both the cards and the tracking payload read this instead, which is
 * what keeps the recorded event honest about what the visitor saw.
 */
export function resolveDisplayedOptions({
    descriptor,
    descriptors,
    location,
    reservedUnitType,
    upSell,
}: ResolveDisplayedOptionsArgs): DisplayedOptions {
    const options = DescriptorOptions(descriptor, descriptors, location);
    const isDescriptorUpsell = options.isDescriptorUpsell();

    const descriptorOptions = isDescriptorUpsell
        ? options.getDescriptorUpsells(descriptors)
        : [];
    const unitTypeOptions = isDescriptorUpsell
        ? []
        : options
            .getUnitTypeUpsells()
            .filter((unitType) => unitType._id !== reservedUnitType._id);

    const hasMultipleOptions = isDescriptorUpsell
        ? descriptorOptions.some((option) => option.unitTypes.some((unitType) => unitType.hasComputedAvailable))
        : unitTypeOptions.some((unitType) => unitType.hasComputedAvailable);

    const nextSizeUpUnitType = upSell
        ? sortUnitTypes(unitOptionsTransformer(upSell), location)[0]
        : undefined;
    const showsNextSizeUp = !hasMultipleOptions
        && !!upSell
        && !!nextSizeUpUnitType
        && upSell.area !== reservedUnitType.area;

    const kind = ((): DisplayedOptionsKind => {
        if (descriptorOptions.length > 0) {
            return 'descriptor';
        }
        if (unitTypeOptions.length > 0) {
            return 'unit-type';
        }
        return showsNextSizeUp ? 'next-size-up' : 'none';
    })();

    return {
        kind,
        descriptorOptions,
        unitTypeOptions,
        nextSizeUp: showsNextSizeUp && upSell && nextSizeUpUnitType
            ? { descriptor: upSell, unitType: nextSizeUpUnitType }
            : null,
        nextSizeUpUnitType,
        hasMultipleOptions,
    };
}
